import { Locales, Timestamp, TranslateFunction } from '@griply/types';
import {
  differenceInCalendarDays,
  format,
  isAfter,
  isSameYear,
} from 'date-fns';

import { getPluralCategory } from './get-plural-category';

export type GetTimeLeftLabelOptions = {
  today: Timestamp;
  t: TranslateFunction;
  language: Locales;
  short?: boolean;
  midShort?: boolean;
  isCompleted?: boolean;
};

export type TimeLeftLabel = {
  label: string;
  isOverdue: boolean;
  isLastWeek: boolean;
  isCompleted: boolean;
};

const resolveKey = (
  base: string,
  { short, midShort }: { short?: boolean; midShort?: boolean },
) => {
  if (short) {
    return `${base}.short.title`;
  }
  if (midShort) {
    return `${base}.mid-short.title`;
  }
  return `${base}.title`;
};

const resolveOverdueKey = (
  base: string,
  { short, midShort }: { short?: boolean; midShort?: boolean },
) => {
  if (short) {
    return `${base}.short`;
  }
  if (midShort) {
    return `${base}.mid-short`;
  }
  return base;
};

// Hermes forces i18next into `compatibilityJSON: 'v3'`, disabling its own plural
// resolution, so the CLDR category is selected here instead.
const withPluralSuffix = (key: string, count: number, language: Locales) =>
  `${key}_${getPluralCategory(count, language)}`;

// Bucket a positive day count into the largest sensible unit and return the
// matching `-left` translation base plus its display value (`amount`) and the
// value used for pluralisation (`rawCount` — fractional for years).
const getTimeBucket = (
  days: number,
): { base: string; amount: number; rawCount: number } => {
  if (days >= 365) {
    const years = days / 365;
    return {
      base: 'general.labels.years-left',
      amount: years % 1 === 0 ? Math.floor(years) : Number(years.toFixed(1)),
      rawCount: years,
    };
  }
  if (days >= 30) {
    const months = Math.floor(days / 30);
    return {
      base: 'general.labels.months-left',
      amount: months,
      rawCount: months,
    };
  }
  if (days >= 15) {
    const weeks = Math.floor(days / 7);
    return {
      base: 'general.labels.weeks-left',
      amount: weeks,
      rawCount: weeks,
    };
  }
  return { base: 'general.labels.days-left', amount: days, rawCount: days };
};

export const getTimeLeftLabel = (
  date: Timestamp,
  { today, t, language, short, midShort, isCompleted }: GetTimeLeftLabelOptions,
): TimeLeftLabel => {
  if (isCompleted) {
    const dateFormat = isSameYear(date, today) ? 'MMM d' : "MMM d, ''yy";
    return {
      label: format(date, dateFormat),
      isOverdue: false,
      isLastWeek: false,
      isCompleted: true,
    };
  }

  const isOverdue = isAfter(today, date);
  const daysLeft = differenceInCalendarDays(date, today);
  const isLastWeek = daysLeft <= 7;

  if (isOverdue) {
    // Overdue buckets like a future date: short shows it negative ("-2w",
    // "-1.5y"); mid-short / full add the localized "ago" wording.
    const { base, amount, rawCount } = getTimeBucket(Math.abs(daysLeft));
    const displayAmount = short ? -amount : amount;
    const displayCount = short ? -rawCount : rawCount;
    return {
      label: t(
        withPluralSuffix(
          resolveOverdueKey(base.replace('-left', '-ago'), { short, midShort }),
          displayCount,
          language,
        ),
        { amount: displayAmount, count: displayCount },
      ),
      isOverdue,
      isLastWeek,
      isCompleted: false,
    };
  }

  if (daysLeft === 0) {
    return {
      label: t('general.labels.dates.today'),
      isOverdue: false,
      isLastWeek: true,
      isCompleted: false,
    };
  }

  const { base, amount, rawCount } = getTimeBucket(daysLeft);
  return {
    label: t(
      withPluralSuffix(
        resolveKey(base, { short, midShort }),
        rawCount,
        language,
      ),
      { amount, count: rawCount },
    ),
    isOverdue,
    isLastWeek,
    isCompleted: false,
  };
};
